import React, { useState, useEffect } from "react";
import {
  Table,
  Tag,
  Button,
  Space,
  Modal,
  DatePicker,
  Input,
  Select,
  message,
} from "antd";
import { UserOutlined, PrinterOutlined, EditOutlined } from "@ant-design/icons"; 
import moment from "moment-timezone";
import jsPDF from "jspdf";
import "jspdf-autotable"; // Required for the table inside PDF
import "antd/dist/antd.css";
import "tailwindcss/tailwind.css";
import OrderDetailsPage from "./orderDetails/OrderDetails";
import { BASE_URL } from "../../constants/Constants";
import axios from "axios";

const { Option } = Select;
const IST = "Asia/Kolkata";

const OrderTable = () => {
  const userId = localStorage.getItem("userId");

  const [orders, setOrders] = useState([]);
  const [originalOrders, setOriginalOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Modals
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [isCustomerModalVisible, setIsCustomerModalVisible] = useState(false);
  
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentBillNo, setCurrentBillNo] = useState(null);
  const [newStatus, setNewStatus] = useState("");

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  /* ================= FETCH DATA ================= */
  const fetchData = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/orders/allOrdersList?size=1000000`
      );
      const ordersData = response.data.content;

      const aggregated = ordersData.reduce((acc, order) => {
        const billNo = order.billNo;
        if (!acc[billNo]) {
          acc[billNo] = {
            ...order,
            products: [],
            totalOrderAmount: order.finalOrderAmount,
          };
        }
        acc[billNo].products.push({
          serialNo: order.serialNo,
          productId: order.productid,
          productName: order.productName,
          quantity: order.orderQuantity,
          price: order.productRate,
          total: order.orderQuantity * order.productRate
        });
        return acc;
      }, {});

      const sorted = Object.values(aggregated).sort(
        (a, b) => moment(b.billDate).valueOf() - moment(a.billDate).valueOf()
      );

      setOrders(sorted);
      setOriginalOrders(sorted);
    } catch (err) {
      console.error("Fetch error", err);
    }
  };

  /* ================= STATUS UPDATE ================= */
  const openStatusModal = (record) => {
    setCurrentBillNo(record.billNo);
    setNewStatus(record.orderStatus);
    setIsStatusModalVisible(true);
  };

  const handleUpdateStatus = async () => {
    try {
      await axios.put(
        `${BASE_URL}/orders/admin/update-status/${currentBillNo}?status=${newStatus}`
      );
      message.success("Status updated");
      setIsStatusModalVisible(false);
      fetchData();
    } catch {
      message.error("Update failed");
    }
  };

  /* ================= HELPERS: CUSTOMER DATA ================= */
  const getCustomerData = (r) => {
    let name = "Unknown";
    let mobile = "N/A";
    let fullAddress = "No Address Data";

    if (r.addressId && typeof r.addressId === "object") {
        name = r.addressId.name;
        mobile = r.addressId.mobile;
        fullAddress = `${r.addressId.houseName}, ${r.addressId.street}, ${r.addressId.city}, ${r.addressId.state} - ${r.addressId.pinCode}`;
    } else if (r.orderAddress && r.orderAddress.includes("@")) {
        const p = r.orderAddress.split("@");
        name = p[1];
        mobile = p[2];
        fullAddress = `${p[3]}, ${p[4]}, ${p[6]}, ${p[7]}`;
    }
    return { name, mobile, fullAddress };
  };

  const showCustomerDetails = (record) => {
      const data = getCustomerData(record);
      setSelectedCustomer(data);
      setIsCustomerModalVisible(true);
  };

  /* ================= PDF GENERATOR (PRINT) ================= */
  const printOrder = (order) => {
    const doc = new jsPDF();
    const { name, mobile, fullAddress } = getCustomerData(order);

    // 1. Header & Title
    doc.setFontSize(20);
    doc.text("INVOICE / ORDER COPY", 105, 15, { align: "center" });
    doc.setFontSize(10);
    doc.text("Divine Depot", 105, 22, { align: "center" });

    // 2. Order Meta Data (Left Side)
    doc.setFontSize(11);
    doc.text(`Order #: ${order.billNo}`, 14, 35);
    doc.text(`Date: ${moment(order.billDate).tz(IST).format("DD-MMM-YYYY h:mm A")}`, 14, 42);
    doc.text(`Payment: ${order.paymentStatus}`, 14, 49);
    doc.text(`Status: ${order.orderStatus}`, 14, 56);

    // 3. Customer Details (Right Side)
    doc.setFont(undefined, 'bold');
    doc.text("Ship To:", 120, 35);
    doc.setFont(undefined, 'normal');
    doc.text(name, 120, 42);
    doc.text(`Ph: ${mobile}`, 120, 49);
    
    // Address wrapping
    const splitAddr = doc.splitTextToSize(fullAddress, 80);
    doc.text(splitAddr, 120, 56);

    // 4. Product Table
    const tableColumn = ["#", "Item Name", "Qty", "Price", "Total"];
    const tableRows = [];

    order.products.forEach((item, index) => {
      const itemData = [
        index + 1,
        item.productName,
        item.quantity,
        `Rs. ${item.price}`,
        `Rs. ${item.total.toFixed(2)}`
      ];
      tableRows.push(itemData);
    });

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 85,
      theme: 'grid',
      headStyles: { fillColor: [66, 66, 66] } // Dark Grey Header
    });

    // 5. Footer & Totals
    const finalY = doc.lastAutoTable.finalY || 90;
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`Grand Total: Rs. ${order.finalOrderAmount.toFixed(2)}`, 190, finalY + 15, { align: "right" });
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(150);
    doc.text("Thank you for your business.", 105, 280, { align: "center" });

    doc.save(`Invoice_${order.billNo}.pdf`);
  };

  /* ================= FILTERS ================= */
  const applyFilters = () => {
    const filtered = originalOrders.filter((o) => {
      const d = moment(o.billDate);
      return (
        (!startDate || d.isSameOrAfter(startDate, "day")) &&
        (!endDate || d.isSameOrBefore(endDate, "day"))
      );
    });
    setOrders(filtered);
  };

  const handleResetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setOrders(originalOrders);
  };

  /* ================= COLUMNS ================= */
  const columns = [
    {
      title: "Bill ID",
      dataIndex: "billNo",
      width: 90,
      render: (t, r) => (
        <span
          className="font-bold text-blue-600 cursor-pointer hover:underline"
          onClick={() => { setSelectedOrder(r); setIsModalVisible(true); }}
        >
          #{t}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "billDate",
      width: 110,
      sorter: (a, b) => moment(a.billDate).valueOf() - moment(b.billDate).valueOf(),
      render: (d) => (
        <div className="leading-tight">
          <div className="font-semibold text-gray-700">{moment(d).tz(IST).format("DD-MMM")}</div>
          <div className="text-xs text-gray-400">{moment(d).tz(IST).format("h:mm A")}</div>
        </div>
      ),
    },
    {
      title: "Customer",
      width: 160, 
      render: (_, r) => {
        const { name } = getCustomerData(r);
        return (
          <div className="flex flex-col items-start">
             <span className="font-bold text-gray-800 mb-1 truncate w-32" title={name}>{name}</span>
             <Button 
                size="small" 
                type="dashed" 
                icon={<UserOutlined />} 
                onClick={() => showCustomerDetails(r)}
                className="text-xs flex items-center"
             >
                View Info
             </Button>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "orderStatus",
      width: 100,
      render: (s) => {
          let color = 'default';
          if(s === 'DELIVERED') color = 'green';
          if(s === 'SHIPPED') color = 'blue';
          if(s === 'CANCELED') color = 'red';
          return <Tag color={color}>{s}</Tag>
      },
    },
    {
      title: "Total",
      dataIndex: "finalOrderAmount",
      width: 100,
      render: (a) => <span className="font-mono font-bold">₹{a?.toFixed(0)}</span>,
    },
    {
      title: "Actions",
      width: 140,
      render: (_, r) => (
        <Space size="small">
          {/* EDIT BUTTON */}
          <Button 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => openStatusModal(r)}
          >Edit
          </Button>
          
          {/* PRINT BUTTON */}
          <Button 
            size="small" 
            type="primary"
            style={{ backgroundColor: '#1677ff' }}
            icon={<PrinterOutlined />} 
            onClick={() => printOrder(r)}
          >
            Print
          </Button>
        </Space>
      ),
    },
  ];

  return userId ? (
    <>
      {/* Top Filter Bar */}
      <div className="flex flex-wrap gap-3 mb-5 p-4 bg-white rounded-lg shadow-sm border border-gray-100 items-center">
        <Input
          placeholder="Search ID"
          className="w-40"
          onChange={(e) => setSearchText(e.target.value)}
        />
        <DatePicker placeholder="Start Date" onChange={setStartDate} />
        <DatePicker placeholder="End Date" onChange={setEndDate} />
        <Button type="primary" style={{ backgroundColor: '#1677ff' }} onClick={applyFilters}>Filter</Button>
        <Button onClick={handleResetFilters}>Reset</Button>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <Table
          rowKey="billNo"
          columns={columns}
          dataSource={orders}
          pagination={{ pageSize: 8 }}
          size="middle"
        />
      </div>

      {/* --- MODAL 1: Order Products Details --- */}
      <Modal
        visible={isModalVisible}
        footer={null}
        onCancel={() => setIsModalVisible(false)}
        width={800}
      >
        {selectedOrder && <OrderDetailsPage order={selectedOrder} />}
      </Modal>

      {/* --- MODAL 2: Customer Contact Details --- */}
      <Modal
        title={<span><UserOutlined className="mr-2"/> Customer Details</span>}
        visible={isCustomerModalVisible}
        footer={[<Button key="close" onClick={() => setIsCustomerModalVisible(false)}>Close</Button>]}
        onCancel={() => setIsCustomerModalVisible(false)}
        width={400}
      >
        {selectedCustomer && (
            <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                    <p className="text-xs text-gray-500 uppercase font-bold">Customer Name</p>
                    <p className="text-lg font-semibold text-gray-800">{selectedCustomer.name}</p>
                </div>
                
                <div className="p-3 bg-blue-50 rounded border border-blue-100">
                    <p className="text-xs text-blue-500 uppercase font-bold">Contact Number</p>
                    <p className="text-lg font-mono font-semibold text-blue-800">
                        {selectedCustomer.mobile}
                    </p>
                </div>

                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                    <p className="text-xs text-gray-500 uppercase font-bold">Shipping Address</p>
                    <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                        {selectedCustomer.fullAddress}
                    </p>
                </div>
            </div>
        )}
      </Modal>

      {/* --- MODAL 3: Update Status --- */}
      <Modal
        visible={isStatusModalVisible}
        onOk={handleUpdateStatus}
        onCancel={() => setIsStatusModalVisible(false)}
        title={`Update Status for #${currentBillNo}`}
      >
        <Select value={newStatus} onChange={setNewStatus} style={{ width: "100%" }}>
          <Option value="ORDERED">ORDERED</Option>
          <Option value="PACKED">PACKED</Option>
          <Option value="SHIPPED">SHIPPED</Option>
          <Option value="DELIVERED">DELIVERED</Option>
          <Option value="CANCELED">CANCELED</Option>
        </Select>
      </Modal>
    </>
  ) : (
    <div className="flex h-screen items-center justify-center text-gray-500 font-medium">
        Please sign in to access the Order Manager.
    </div>
  );
};

export default OrderTable;